/* CmdbViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./CmdbViewRenderer");
const _ = require("lodash");
// const CATALOG_TENANT_ID = "02fd0bdc-dd20-44a0-8e27-73e56c11643a";

/**
 * class CmdbViewComponent
 *
 * @fires CmdbViewComponent#OrgChanged
 */
class CmdbViewComponent extends EventEmitter {
    /**
     *
     * @param {UserSession} userSession - Session object for the current user.
     * @param {UiUtils} uiUtils - Utility functions for user interface interactions.
     * @param {CoreLogger} logger - Logging utility for logging warnings, errors, and information.
     * @param {UserFeedback} userFeedback - User feedback system for displaying veils to users.
     * @param {OrganizationService} OrganizationService - Service to handle organization data.
     * @param {SteveIo} steveIo - I/O Steve service.
     * @param {DataSyncConfig} DataSyncConfig - Entity for CMDB sync configuration.
     * @param {ServiceConnection} ServiceConnection - Entity for managing connections to external services.
     * @param {Device} DeviceEntity - Entity definition for managing devices.
     * @param {Explorables} Explorables - Entity definition for managing explorables.
     * @param {Site} SiteEntity - Entity definition for managing sites.
     * @param {ValueMap} ValueMap - Entity for mapping and managing value mappings such as vendor and OS mappings.
     * @param {StorageFactory} storageFactory - Factory for managing local or session storage operations.
     * @param {Router} Router - Routing service to navigate between different application views.
     * @param {JnibAdapter} jnibAdapter - JNIB Adapter.
     */
    constructor(
        userSession,
        uiUtils,
        logger,
        userFeedback,
        OrganizationService,
        steveIo,
        DataSyncConfig,
        ServiceConnection,
        DeviceEntity,
        CustomFieldsEntity,
        Explorables,
        SiteEntity,
        ValueMap,
        storageFactory,
        Router,
        jnibAdapter
    ) {
        super();

        this._userSession = userSession;
        this._uiUtils = uiUtils;
        this._logger = logger;
        this._muf = userFeedback;
        this._organizationService = OrganizationService;
        this._steveIo = steveIo;
        this._DataSyncConfigEntityDef = DataSyncConfig;
        this._ServiceConnectionEntityDef = ServiceConnection;
        this._DeviceEntityDef = DeviceEntity;
        this._CustomFieldsEntityDef = CustomFieldsEntity;
        this._ExplorablesEntityDef = Explorables;
        this._SiteEntityDef = SiteEntity;
        this._ValueMapEntityDef = ValueMap;
        this._storageFactory = storageFactory;
        this._router = Router;
        this._nibApiService = jnibAdapter.getNibApiService();

        this._snDataSyncConfigEntity = undefined;
        this._nbDataSyncConfigEntity = undefined;
        this._xdDataSyncConfigEntity = undefined;

        this._nbServiceConnectionEntities = [];
        this._snServiceConnectionEntities = [];
        this._xdServiceConnectionEntities = [];

        this._organizationData = {};
        this._nodePropData = {};
        this._vendorOsMap = {};
        this._noVendorMap = false;
        this._deviceTypeBase = {};

        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        let shutdowns = [this._renderer.shutdown()];
        return Promise.all(shutdowns).then(
            () => {
                this._DataSyncConfigEntityDef = undefined;
                this._ServiceConnectionTypeEntityDef = undefined;
                this._ServiceConnectionEntityDef = undefined;
                this._DeviceEntityDef = undefined;
                this._CustomFieldsEntityDef = undefined;
                this._ExplorablesEntityDef = undefined;
                this._SiteEntityDef = undefined;
                this._ValueMapEntityDef = undefined;
                this._storageFactory = undefined;
                this._router = undefined;
                this._nibApiService = undefined;

                this._snDataSyncConfigEntity = undefined;
                this._nbDataSyncConfigEntity = undefined;
                this._xdDataSyncConfigEntity = undefined;
                this._nbServiceConnectionEntities = undefined;
                this._snServiceConnectionEntities = undefined;
                this._xdServiceConnectionEntities = undefined;

                this._organizationData = undefined;
                this._nodePropData = undefined;
                this._vendorOsMap = undefined;
                this._noVendorMap = undefined;
                this._deviceTypeBase = undefined;
            },
            (err) => {
                console.log("Error shutting down CmdbViewComponent: ", err);
            }
        );
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getActiveOrgId() {
        return _.get(this._userSession, "activeOrganization.id");
    }

    get activeOrgName() {
        return this.getActiveOrgName();
    }

    getActiveOrgName() {
        return _.get(this._userSession, "activeOrganization.name");
    }

    async whenModelIsReady() {
        try {
            await this._loadAllData();
        } catch (err) {
            this._logger.warn(err, "CmdbViewComponent.whenModelIsReady() error.");
        }
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    async organizationChanged() {
        /**
         * Emitted when the active organization changes.
         * @event CmdbViewComponent#OrgChanged
         */
        try {
            await this._loadAllData();
            this.emit("OrgChanged"); // Emit event when org is changed
        } catch (err) {
            this._logger.warn(err, "CmdbViewComponent.organizationChanged() error.");
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    async _loadAllData() {
        try {
            // Wait for initial set of asynchronous calls to resolve
            await Promise.all([
                this._loadOrganizationData(),
                this._loadDataSyncEntity(),
                this._loadServiceConnections(),
                this._loadVendorOsMap(),
            ]);

            // Sequentially load remaining data
            await this._loadDevicePropsData();
            this._loadDeviceTypes();
        } catch (err) {
            this._logger.warn(err, "CmdbViewComponent._loadAllData() error.");
            throw err;
        }
    }

    async _loadOrganizationData() {
        try {
            this._organizationData = await this._organizationService.getById({
                id: this.activeOrgId,
            });
            return this._organizationData;
        } catch (err) {
            this._logger.warn(err, "CmdbViewComponent._loadOrganizationData() error.");
            throw err;
        }
    }

    async _loadDevicePropsData() {
        try {
            this._nodePropData = await this._snDataSyncConfigEntity.loadSyncableDeviceProps();
            this._deviceNodeTreeData = this._generateTreeDataForNodeProps(this._nodePropData);
        } catch (err) {
            this._logger.warn(err, "CmdbViewComponent._loadDevicePropsData() error.");
            throw err;
        }
    }

    async _loadServiceConnections() {
        try {
            const query = {
                $or: [
                    {serviceConnectionTypeName: "svcConnection/netBox.svcConnection"},
                    {serviceConnectionTypeName: "svcConnection/serviceNow.svcConnection"},
                    {serviceConnectionTypeName: "svcConnection/xdome.svcConnection"},
                ],
            };
            const serviceConnectionEntities =
                await this._ServiceConnectionEntityDef.loadAscendServiceConnections(
                    this.activeOrgId,
                    query
                );
            // Reset serviceConnections
            this._nbServiceConnectionEntities = [];
            this._snServiceConnectionEntities = [];
            this._xdServiceConnectionEntities = [];

            serviceConnectionEntities.forEach((entity) => {
                if (entity.serviceConnectionTypeName === "svcConnection/netBox.svcConnection") {
                    this._nbServiceConnectionEntities.push(entity);
                } else if (
                    entity.serviceConnectionTypeName === "svcConnection/serviceNow.svcConnection"
                ) {
                    this._snServiceConnectionEntities.push(entity);
                } else if (
                    entity.serviceConnectionTypeName === "svcConnection/xdome.svcConnection"
                ) {
                    this._xdServiceConnectionEntities.push(entity);
                }
            });
        } catch (err) {
            this._logger.warn(err, "CmdbViewComponent._loadServiceConnections error.");
            throw err;
        }
    }

    async _loadDataSyncEntity() {
        try {
            // Load ServiceNow Data Sync Config
            this._snDataSyncConfigEntity = await this._DataSyncConfigEntityDef.loadProviderConfig(
                this.activeOrgId,
                "serviceNow",
                "cmdb"
            );

            if (_.isEmpty(this._snDataSyncConfigEntity)) {
                this._snDataSyncConfigEntity = new this._DataSyncConfigEntityDef(this.activeOrgId);
                this._snDataSyncConfigEntity.setDataProvider("serviceNow");
                this._snDataSyncConfigEntity.setDataProviderProduct("cmdb");
            }

            // Load NetBox Data Sync Config
            this._nbDataSyncConfigEntity = await this._DataSyncConfigEntityDef.loadProviderConfig(
                this.activeOrgId,
                "netBox",
                "cmdb"
            );

            if (_.isEmpty(this._nbDataSyncConfigEntity)) {
                this._nbDataSyncConfigEntity = new this._DataSyncConfigEntityDef(this.activeOrgId);
                this._nbDataSyncConfigEntity.setDataProvider("netBox");
                this._nbDataSyncConfigEntity.setDataProviderProduct("cmdb");
                this._nbDataSyncConfigEntity.setInstanceLinkingCriteria({
                    deviceLinking: {
                        name: "name",
                    },
                });
            }

            // Load xDome Data Sync Config
            this._xdDataSyncConfigEntity = await this._DataSyncConfigEntityDef.loadProviderConfig(
                this.activeOrgId,
                "claroty",
                "xdome"
            );

            if (_.isEmpty(this._xdDataSyncConfigEntity)) {
                this._xdDataSyncConfigEntity = new this._DataSyncConfigEntityDef(this.activeOrgId);
                this._xdDataSyncConfigEntity.setDataProvider("claroty");
                this._xdDataSyncConfigEntity.setDataProviderProduct("xdome");
                // this._xdDataSyncConfigEntity.setInstanceLinkingCriteria({
                //     deviceLinking: {
                //         name: "name",
                //     },
                // });
            }
        } catch (err) {
            this._logger.warn(err, "CmdbViewComponent._loadDataSyncEntity() error.");
            throw err;
        }
    }

    _loadDeviceTypes() {
        this._deviceTypeBase = this._DeviceEntityDef.getDetectedRawTypeBaseLabels();
    }

    async _loadVendorOsMap() {
        try {
            const entity = await this._ValueMapEntityDef.loadVendorOsMapEntity(this.activeOrgId);
            if (entity) {
                this._vendorOsMap = entity.getValueMap();
            } else {
                this._noVendorMap = true;
                this.emit("NoVendorMap");
            }
        } catch (err) {
            this._logger.warn(err, "CmdbViewComponent._loadVendorOsMap() error.");
            throw err;
        }
    }

    // Device Node Prop Tree Data generation - Start

    _generateTreeDataForNodeProps(explorables) {
        return Object.entries(explorables).map(([key, value]) =>
            this._generateTreeNodeForNodeProps(key, value)
        );
    }

    _generateTreeNodeForNodeProps(name, nodeData) {
        if (!nodeData.path) {
            const node = {};
            node.text = name;
            node.children = [];
            for (let key in nodeData) {
                node.children.push(this._generateTreeNodeForNodeProps(key, nodeData[key]));
            }
            return node;
        }
        return name;
    }

    // Device Node Prop Tree Data generation - End

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = CmdbViewComponent;
CmdbViewComponent.$groupName = "Organization";
CmdbViewComponent.$displayName = "CMDB Integration";
CmdbViewComponent.$license = ["GLUWARE"];
CmdbViewComponent.$inject = [
    "$teve.userSession",
    "$teve.ui.UiUtils",
    "$teve.logger",
    "$teve.ui.userFeedback",
    "$teve.services.instance.Organization",
    "$teve.io",
    "$teve.entities.DataSyncConfig",
    "$teve.entities.ServiceConnection",
    "$teve.entities.Device",
    "$teve.entities.CustomFields",
    "$teve.entities.Explorables",
    "$teve.entities.Site",
    "$teve.entities.ValueMap",
    "$teve.storageFactory",
    "$teve.router",
    "$teve.jnibAdapter",
];
